// client/src/components/tendermaker/RequirementActionBar.js
import React, { useState } from "react";

const btn = {
  padding: "4px 8px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 10,
};
const btn2 = { ...btn, background: "#fafafa" };
const btnPrimary = { ...btn, background: "#2563eb", color: "#fff", borderColor: "#2563eb" };
const btnSmall = { ...btn, fontSize: 9, padding: "2px 6px" };
const tiny = { color: "#6b7280", fontSize: 10 };

function isPdfName(name = "") {
  return String(name).split(".").pop()?.toLowerCase() === "pdf";
}
function isDocxName(name = "") {
  const ext = String(name).split(".").pop()?.toLowerCase();
  return ext === "docx" || ext === "doc";
}

// Inline editable file name component
function EditableFileName({ fileName, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(fileName);

  if (!editing) {
    return (
      <span
        style={{ flex: 1, fontWeight: 600, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
        title="Click to rename"
        onClick={() => { setValue(fileName); setEditing(true); }}
      >
        {fileName}
      </span>
    );
  }

  return (
    <input
      autoFocus
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        const trimmed = value.trim();
        if (trimmed && trimmed !== fileName) onSave(trimmed);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          const trimmed = value.trim();
          if (trimmed && trimmed !== fileName) onSave(trimmed);
          setEditing(false);
        }
        if (e.key === "Escape") setEditing(false);
      }}
      style={{
        flex: 1, minWidth: 0, fontWeight: 600, fontSize: 9,
        padding: "1px 3px", borderRadius: 3, border: "1px solid #93c5fd",
        outline: "none", background: "#eff6ff",
      }}
    />
  );
}

export default function RequirementActionBar({
  requirement,
  item,
  bidDocs,
  profileDocs,
  // branding
  branding,
  setBranding,
  // file ops
  onUploadForItem,
  onUploadFilesForItem,
  onDownloadItemFile,
  onDownloadFile,
  onViewFile,
  onRemoveFile,
  onRenameFile,
  onToggleRemove,
  onAttachVault,
  onPromoteItemToProfile,
  onLoadEvidenceToEditor,
  onLoadAttachedDocToEditor,
  onApplyBrandingToKeys,
  onApplyBrandingInline,
  editorPdfKey,
  onAddNewDoc,
  onMergeItemFiles,
  onClickEvidence,
  onSetErr,
}) {
  const [showFiles, setShowFiles] = useState(false);
  const [showMore, setShowMore] = useState(false);
  // Track which PDFs are selected for merge (by fileId)
  const [mergeSelected, setMergeSelected] = useState({});

  if (!requirement) {
    return (
      <div
        style={{
          padding: "10px 12px",
          textAlign: "center",
          color: "#9ca3af",
          fontSize: 12,
          fontWeight: 600,
          background: "#f9fafb",
          borderRadius: 10,
          border: "1px solid #e5e7eb",
        }}
      >
        Select a requirement chip to begin.
      </div>
    );
  }

  const r = requirement;
  const it = item;
  const hasKey = Boolean(it?.sourceRef?.key);
  const fileName = it?.sourceRef?.fileName || "";
  const docxAttached = hasKey && isDocxName(fileName);
  const sources = Array.isArray(it?.sources) ? it.sources : [];
  const evidence = Array.isArray(r.evidence) ? r.evidence : [];
  const files = Array.isArray(it?.files) ? it.files : [];

  const pdfFiles = files.filter((f) => isPdfName(f.fileName) && f.key);
  const otherFiles = files.filter((f) => !isPdfName(f.fileName) || !f.key);

  const statusLabel = it?.sourceType
    ? it.sourceType === "draft"
      ? "Draft"
      : it.sourceType === "uploaded"
      ? "File attached"
      : it.sourceType
    : "No content";

  const allPdfKeys = pdfFiles.map((f) => f.key);

  // Merge selection helpers
  const selectedMergeKeys = pdfFiles.filter((f) => mergeSelected[f.fileId]).map((f) => f.key);
  function toggleMerge(fileId) {
    setMergeSelected((prev) => {
      const next = { ...prev };
      if (next[fileId]) delete next[fileId];
      else next[fileId] = true;
      return next;
    });
  }
  function selectAllForMerge() {
    const next = {};
    pdfFiles.forEach((f) => { next[f.fileId] = true; });
    setMergeSelected(next);
  }

  return (
    <div
      style={{
        background: "#f9fafb",
        borderRadius: 10,
        border: "1px solid #e5e7eb",
        padding: "5px 8px",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Row 1: Header + status + Upload + Files toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 4 }}>
          <span
            style={{
              display: "inline-block",
              padding: "1px 5px",
              borderRadius: 6,
              background:
                r.type === "annexure" ? "#dbeafe" : r.type === "unknown" ? "#fef3c7" : r.type === "custom" ? "#f3e8ff" : "#f0fdf4",
              fontWeight: 700,
              fontSize: 8,
            }}
          >
            {r.type}
          </span>
          <span style={{ fontWeight: 800, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {r.label}
          </span>
          <span style={{ ...tiny, fontSize: 9, flexShrink: 0 }}>
            {statusLabel}
            {files.length > 0 ? ` \u2022 ${files.length} file${files.length > 1 ? "s" : ""}` : ""}
          </span>
        </div>

        {/* Upload button */}
        <label style={{ ...btn2, cursor: "pointer", display: "inline-flex", alignItems: "center" }}>
          Upload
          <input
            type="file"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              const input = e.target;
              const fileList = input.files;
              if (!fileList?.length) return;
              // Copy files before clearing input
              const files = Array.from(fileList);
              if (onUploadFilesForItem) {
                onUploadFilesForItem(r.itemId, files)
                  .catch((x) => onSetErr(String(x?.message || x)))
                  .finally(() => { input.value = ""; });
              } else if (files[0]) {
                onUploadForItem(r.itemId, files[0])
                  .catch((x) => onSetErr(String(x?.message || x)))
                  .finally(() => { input.value = ""; });
              }
            }}
          />
        </label>

        {/* Files toggle */}
        {files.length > 0 && (
          <button style={btn2} onClick={() => setShowFiles((v) => !v)}>
            Files ({files.length}) {showFiles ? "\u25B2" : "\u25BC"}
          </button>
        )}

        {/* More toggle */}
        <button style={btn2} onClick={() => setShowMore((v) => !v)}>
          {showMore ? "Less" : "More\u2026"}
        </button>

        {/* Remove */}
        <button
          style={{ ...btn2, color: "#dc2626", borderColor: "#fca5a5", fontSize: 9, padding: "2px 5px" }}
          onClick={() => onToggleRemove(r.itemId)}
          title="Remove this requirement"
        >
          {"\u00d7"}
        </button>
      </div>

      {/* Collapsible file list */}
      {showFiles && files.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 160, overflowY: "auto" }}>
          {/* PDF files with merge checkboxes */}
          {pdfFiles.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ ...tiny, fontWeight: 700, fontSize: 8 }}>PDF Files:</span>
                {pdfFiles.length >= 2 && (
                  <button style={{ ...btnSmall, fontSize: 7, padding: "1px 4px" }} onClick={selectAllForMerge}>
                    Select All
                  </button>
                )}
              </div>
              {pdfFiles.map((f, idx) => {
                const fName = f.fileName || f.fileId || `PDF ${idx + 1}`;
                const isChecked = !!mergeSelected[f.fileId];
                return (
                  <div key={f.fileId || idx} style={{ display: "flex", alignItems: "center", gap: 3, padding: "1px 5px", borderRadius: 5, background: isChecked ? "#eff6ff" : "#fff", border: isChecked ? "1px solid #93c5fd" : "1px solid #e5e7eb", fontSize: 9 }}>
                    {pdfFiles.length >= 2 && (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleMerge(f.fileId)}
                        style={{ cursor: "pointer", flexShrink: 0 }}
                        title="Select for merge"
                      />
                    )}
                    <EditableFileName
                      fileName={fName}
                      onSave={(newName) => onRenameFile && onRenameFile(r.itemId, f.fileId, newName)}
                    />
                    <span style={{ padding: "0px 3px", borderRadius: 3, background: "#fee2e2", fontWeight: 700, fontSize: 7, flexShrink: 0 }}>PDF</span>
                    {onViewFile && <button style={btnSmall} onClick={() => onViewFile(r.itemId, f)}>View</button>}
                    {onDownloadFile && f.key && <button style={btnSmall} onClick={() => onDownloadFile(r.itemId, f)}>{"\u2193"}</button>}
                    {onRemoveFile && <button style={{ ...btnSmall, color: "#dc2626", borderColor: "#fca5a5" }} onClick={() => onRemoveFile(r.itemId, f.fileId)}>{"\u00d7"}</button>}
                  </div>
                );
              })}
              {pdfFiles.length >= 2 && onMergeItemFiles && (
                <button
                  style={{ ...btnPrimary, fontSize: 8, padding: "2px 6px", alignSelf: "flex-start" }}
                  onClick={() => {
                    const keys = selectedMergeKeys.length >= 2 ? selectedMergeKeys : allPdfKeys;
                    onMergeItemFiles(r.itemId, keys);
                  }}
                  title={selectedMergeKeys.length >= 2 ? `Merge ${selectedMergeKeys.length} selected PDFs` : "Select PDFs or merge all"}
                >
                  {selectedMergeKeys.length >= 2
                    ? `Merge ${selectedMergeKeys.length} Selected`
                    : `Merge All ${pdfFiles.length} PDFs`}
                </button>
              )}
            </>
          )}

          {/* Other files */}
          {otherFiles.length > 0 && (
            <>
              <span style={{ ...tiny, fontWeight: 700, fontSize: 8 }}>Other Files:</span>
              {otherFiles.map((f, idx) => {
                const fName = f.fileName || f.fileId || `File ${idx + 1}`;
                const isDocx = isDocxName(fName);
                return (
                  <div key={f.fileId || idx} style={{ display: "flex", alignItems: "center", gap: 3, padding: "1px 5px", borderRadius: 5, background: "#fff", border: "1px solid #e5e7eb", fontSize: 9 }}>
                    <EditableFileName
                      fileName={fName}
                      onSave={(newName) => onRenameFile && onRenameFile(r.itemId, f.fileId, newName)}
                    />
                    <span style={{ padding: "0px 3px", borderRadius: 3, background: isDocx ? "#dbeafe" : "#f3f4f6", fontWeight: 700, fontSize: 7 }}>{isDocx ? "DOCX" : f.type || "FILE"}</span>
                    {onViewFile && f.key && <button style={btnSmall} onClick={() => onViewFile(r.itemId, f)}>View</button>}
                    {onDownloadFile && f.key && <button style={btnSmall} onClick={() => onDownloadFile(r.itemId, f)}>{"\u2193"}</button>}
                    {onRemoveFile && <button style={{ ...btnSmall, color: "#dc2626", borderColor: "#fca5a5" }} onClick={() => onRemoveFile(r.itemId, f.fileId)}>{"\u00d7"}</button>}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      {/* Legacy single-file display */}
      {files.length === 0 && hasKey && (
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <span style={{ ...tiny, fontWeight: 600, fontSize: 9 }}>{fileName}</span>
          <button style={btnSmall} onClick={() => onDownloadItemFile(r.itemId)}>Download</button>
        </div>
      )}

      {/* More actions: branding, vault, annexure etc */}
      {showMore && (
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Branding controls */}
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap", padding: "3px 0" }}>
            <span style={{ fontWeight: 700, fontSize: 9, color: "#374151" }}>Branding:</span>
            <label style={{ display: "flex", gap: 2, alignItems: "center", fontWeight: 600, fontSize: 9 }}>
              <input type="checkbox" checked={branding.includeSignature} onChange={(e) => setBranding((p) => ({ ...p, includeSignature: e.target.checked }))} />
              Sign
            </label>
            <label style={{ display: "flex", gap: 2, alignItems: "center", fontWeight: 600, fontSize: 9 }}>
              <input type="checkbox" checked={branding.includeStamp} onChange={(e) => setBranding((p) => ({ ...p, includeStamp: e.target.checked }))} />
              Stamp
            </label>
            <label style={{ display: "flex", gap: 2, alignItems: "center", fontWeight: 600, fontSize: 9 }}>
              <input type="checkbox" checked={branding.includeLetterhead} onChange={(e) => setBranding((p) => ({ ...p, includeLetterhead: e.target.checked }))} />
              Letterhead
            </label>
            {onApplyBrandingInline && (
              <button
                style={{ ...btnPrimary, fontSize: 9, padding: "2px 6px" }}
                onClick={onApplyBrandingInline}
                title={editorPdfKey ? "Apply to active PDF" : "View a PDF first"}
                disabled={!editorPdfKey}
              >
                Apply to PDF
              </button>
            )}
            {allPdfKeys.length > 0 && onApplyBrandingToKeys && (
              <button style={{ ...btn2, fontSize: 9, padding: "2px 6px" }} onClick={() => onApplyBrandingToKeys(allPdfKeys)}>
                Brand All ({allPdfKeys.length})
              </button>
            )}
          </div>

          {/* Other actions */}
          <div style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
            {onAddNewDoc && (
              <button style={btn2} onClick={() => onAddNewDoc(r.itemId, r.label)}>New Doc</button>
            )}
            {r.type === "annexure" && (
              <button style={btn2} onClick={() => onLoadEvidenceToEditor(r.itemId)}>Load Annexure</button>
            )}
            {hasKey && docxAttached && (
              <button style={btn2} onClick={() => onLoadAttachedDocToEditor(r.itemId)}>Load DOCX</button>
            )}
            {hasKey && (
              <button style={btn2} onClick={() => onPromoteItemToProfile(r.itemId)}>Promote to Vault</button>
            )}
            <select
              defaultValue=""
              onChange={(e) => e.target.value && onAttachVault(r.itemId, e.target.value)}
              style={{ ...btn2, cursor: "pointer", fontSize: 9, padding: "3px 5px" }}
            >
              <option value="">Attach Vault Doc...</option>
              {(profileDocs || []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label || d.masterId} {"\u2022"} {d.fileName || d.id}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Evidence chips */}
      {evidence.length > 0 && (
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ ...tiny, fontWeight: 700, fontSize: 8 }}>Evidence:</span>
          {evidence.slice(0, 8).map((ev, idx) => {
            const src = ev?.source;
            const hasDirect = !!(src?.page && (src?.docId || src?.fileId));
            const canClick = hasDirect || !!String(ev?.text || "").trim();
            return (
              <button
                key={idx}
                style={{
                  padding: "1px 5px",
                  borderRadius: 999,
                  border: "1px solid #e5e7eb",
                  background: hasDirect ? "#dcfce7" : canClick ? "#fff" : "#f3f4f6",
                  cursor: canClick ? "pointer" : "default",
                  fontWeight: 700,
                  fontSize: 8,
                }}
                onClick={() => canClick && onClickEvidence(ev)}
                title={ev?.text ? ev.text.slice(0, 200) : "No evidence text"}
                disabled={!canClick}
              >
                E{idx + 1}
                {hasDirect ? ` p${src.page}` : ""}
              </button>
            );
          })}
        </div>
      )}

      {/* Source chips */}
      {sources.length > 0 && (
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ ...tiny, fontWeight: 700, fontSize: 8 }}>Sources:</span>
          {sources.slice(0, 6).map((s, idx) => (
            <button
              key={idx}
              style={{
                padding: "1px 5px",
                borderRadius: 999,
                border: "1px solid #e5e7eb",
                background: "#fafafa",
                cursor: "pointer",
                fontWeight: 700,
                fontSize: 8,
              }}
              onClick={() => {
                const docObj = bidDocs.find((d) => d.id === s.docId);
                if (!docObj) return onSetErr("Source doc not found.");
                onClickEvidence({ text: s.quote || "", source: { docId: s.docId, page: s.page } });
              }}
              title={s.quote || "Jump to source page"}
            >
              {(bidDocs.find((d) => d.id === s.docId)?.fileName || "Doc").slice(0, 12)} p{s.page || 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
